/*
 * Copyright (c) 2023. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import {
  CheckCircleRounded,
  ChevronLeft,
  ChevronRight,
  DoNotDisturb as DoNotDisturbIcon,
  ErrorRounded as ErrorOutlineIcon,
  InfoRounded,
  KeyboardReturn as KeyboardReturnIcon,
  Notifications as NotificationsIcon,
  Refresh as RefreshIcon,
  RemoveRedEye as RemoveRedEyeIcon,
  WarningRounded,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Chip,
  Container,
  Divider,
  Grid,
  type IconProps,
  Paper,
  Stack,
  type Theme,
  Typography,
} from '@mui/material';
import {
  type AbsoluteURL,
  formatDateLocal,
  type ListResultsQuery,
  NotificationSeverity,
  type ObjectIdMap,
  SalesforceObject,
  type UINotification,
} from '@poweralpha/common';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { HTTPError } from 'ky';
import { isEqual, min } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  clearAllNotification,
  getNotifications,
  markAllNotificationAsViewed,
  markNotificationAsViewed,
} from '../api';
import { getbnCRMObjectIdMap } from '../api/bncrm';
import { useAuth } from '../auth';
import { LoadingButton, LoadingIconButton } from '../react-components/buttons';
import ConfirmDialog, { type ConfirmDialogQueryParams } from '../react-components/ConfirmDialog';
import { Emoji } from '../react-components/Emoji';
import StateAutocomplete from '../react-components/inputs/StateAutocomplete';
import usePANavigate from '../react-components/navigate/usePANavigate';
import { usePageTitle } from '../react-components/usePageTitle';
import { WHITESPACE } from '../utils';
import { formatNotifications, getNotifSx } from './utils';

const PAGE_SIZE = 25;
const REFETCH_INTERVAL = 40; // secs

const paperSx = (theme: Theme) => ({
  bgcolor: theme.palette.background.default,
  marginBottom: 1,
  borderRadius: 2,
  paddingLeft: 1,
  paddingRight: 1,
});

export interface UINotificationsQueryResult extends ListResultsQuery<UINotification> {
  unread: Array<UINotification>;
}

export const NotificationIcon = ({
  severity,
  fontSize = 'small',
}: {
  severity: UINotification['severity'];
  fontSize?: IconProps['fontSize'];
}) => {
  switch (severity) {
    case 'error':
      return <ErrorOutlineIcon color={severity} fontSize={fontSize} />;
    case 'warning':
      return <WarningRounded color={severity} fontSize={fontSize} />;
    case 'success':
      return <CheckCircleRounded color={severity} fontSize={fontSize} />;
    default:
      return <InfoRounded color={severity} fontSize={fontSize} />;
  }
};

function Notifications() {
  const { navigate, updateState, location } = usePANavigate<
    { refreshNotifCenter?: boolean } & ConfirmDialogQueryParams
  >();
  const { user } = useAuth();
  const redirectTo = new URLSearchParams(location.search).get('return') as AbsoluteURL | null;
  const idsRef = useRef<Array<string>>([]);

  const [ids, setIds] = useState<Array<string>>([]);
  const [page, setPage] = useState(0);
  const [severity, setSeverity] = useState<NotificationSeverity | null>(null);

  const projectIdMapQuery = useQuery<ObjectIdMap>({
    queryKey: ['project-id-map'],
    queryFn: () => getbnCRMObjectIdMap(SalesforceObject.project),
  });
  const projectIdMap = projectIdMapQuery.data;

  const { data, refetch, isLoading } = useQuery<UINotificationsQueryResult>({
    queryKey: ['notifications', PAGE_SIZE, page, severity],
    queryFn: () =>
      getNotifications(PAGE_SIZE, page * PAGE_SIZE, severity).then((res) =>
        formatNotifications(res, projectIdMap, user?.email),
      ),
    enabled: Boolean(user) && Boolean(projectIdMap),
    refetchInterval: REFETCH_INTERVAL * 1e3,
  });

  const refreshNotifCenter = useCallback(
    (path?: AbsoluteURL) => {
      navigate(path ?? `.${location.search}`, { refreshNotifCenter: true }, true);
    },
    [location.search, navigate],
  );

  const markAsViewedMutation = useMutation<void, HTTPError, string>({
    mutationFn: (id) => markNotificationAsViewed(id),
    onSuccess: () => refetch(),
  });
  const markAsViewed = markAsViewedMutation.mutate;

  const markAllAsViewedMutation = useMutation<{ count: number }>({
    mutationFn: markAllNotificationAsViewed,
    onSuccess: () => {
      refetch();
      refreshNotifCenter();
    },
  });
  const markAllAsViewed = markAllAsViewedMutation.mutate;

  const clearAllMutation = useMutation<{ count: number }>({
    mutationFn: clearAllNotification,
    onSuccess: () => {
      refetch();
      refreshNotifCenter();
    },
  });
  const clearAll = clearAllMutation.mutate;

  const handleNotifClick = useCallback(
    ({ link, id }: UINotification) => {
      markAsViewed(id);
      refreshNotifCenter(link as AbsoluteURL);
    },
    [markAsViewed, refreshNotifCenter],
  );

  const handleClearAll = useCallback(async () => clearAll(), [clearAll]);

  const handleOlderClick = useCallback(() => setPage((prev) => prev + 1), []);

  const handleNewerClick = useCallback(() => setPage((prev) => prev - 1), []);

  const handleRefresh = useCallback(() => refetch(), [refetch]);

  const handleReturn = useCallback(() => {
    if (redirectTo) {
      navigate(redirectTo);
      return;
    }
    navigate(-1);
  }, [navigate, redirectTo]);

  const handleClearAllClick = useCallback(
    () => updateState({ confirmDialogOpen: true }),
    [updateState],
  );

  const handleSeverityChange = useCallback(
    (_: React.SyntheticEvent<Element, Event>, value: string | string[] | null) =>
      setSeverity(value as NotificationSeverity | null),
    [],
  );

  const noNotifications = useMemo(() => data?.count === 0, [data?.count]);

  const notificationEntries = useMemo(
    () =>
      (data?.rows ?? []).map((notif) => (
        <>
          <Box
            key={`notif-entry-${notif.id}`}
            id={notif.id}
            onClick={() => handleNotifClick(notif)}
            style={{ cursor: 'pointer' }}
            sx={[
              {
                color: notif.severity,
                bgcolor: notif.severity,
              },
              (theme) => getNotifSx(theme, notif),
            ]}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{
                minHeight: 50,
                padding: 0.5,
                paddingRight: 1,
                paddingLeft: 1,
                alignItems: 'flex-start',
              }}
            >
              <Box sx={{ pt: 0.25 }}>
                <NotificationIcon severity={notif.severity} fontSize="medium" />
              </Box>
              <Stack
                sx={{
                  width: '100%',
                }}
              >
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 'fontWeightBold',
                    }}
                  >
                    {notif.subject}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
                    {formatDateLocal(notif.created_at)}
                  </Typography>
                </Stack>
                <Typography
                  sx={{
                    fontSize: 16,
                  }}
                >
                  {notif.notif_content}
                </Typography>
                <Typography
                  hidden={!notif.message}
                  sx={{
                    color: `${notif.severity}.main`,
                    fontSize: 13,
                  }}
                >
                  {notif.severity === NotificationSeverity.ERROR ? 'Error: ' : 'Comments: '}
                  {notif.message}
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
                  {notif.whenStr ?? ''}
                  {notif.whenStr ? ' • ' : ''}Click to view
                </Typography>
              </Stack>
            </Stack>
          </Box>
          <Divider key={`notif-entry-${notif.id}-divider`} />
        </>
      )),
    [data?.rows, handleNotifClick],
  );

  useEffect(() => {
    if (ids.length === 0) return;
    if (idsRef.current.length === 0) {
      idsRef.current = ids;
      return;
    }
    if (!isEqual(ids, idsRef.current)) {
      refreshNotifCenter();
      idsRef.current = ids;
    }
  }, [ids, refreshNotifCenter]);

  useEffect(() => {
    if (data) {
      const newIds = data.rows.map(({ id }) => id);
      setIds(newIds);
    }
  }, [data]);

  usePageTitle(`Notifications${(data?.unread.length ?? 0) > 0 ? ` (${data?.unread.length})` : ''}`);

  return (
    <>
      <ConfirmDialog
        onConfirm={handleClearAll}
        redirectPath="."
        label="Clear"
        color="error"
        connectorWord="all"
        itemType="notifications"
      />
      <Container maxWidth="lg" sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
        <Stack
          spacing={1}
          sx={{
            flex: 1,
            marginBottom: 2,
            minHeight: 0,
          }}
        >
          <Grid>
            <Chip label="Return" icon={<KeyboardReturnIcon />} onClick={handleReturn} />
          </Grid>
          <Paper
            elevation={3}
            sx={[
              paperSx,
              {
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
              },
            ]}
          >
            <Stack
              sx={{
                flex: 1,
                minHeight: 0,
                paddingBottom: 1,
                paddingTop: 1,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  paddingLeft: 1,
                  paddingRight: 1,
                  minHeight: 50,
                  paddingBottom: 0.5,
                  alignItems: 'center',
                }}
              >
                <Badge
                  badgeContent={data?.unread.length}
                  color="error"
                  overlap="circular"
                  max={999}
                >
                  <NotificationsIcon fontSize="large" />
                </Badge>
                <Typography
                  variant="h4"
                  sx={{
                    paddingLeft: 1,
                    fontWeight: 'fontWeightBold',
                  }}
                >
                  Notifications
                </Typography>
                <LoadingIconButton
                  loading={isLoading}
                  size="large"
                  color="primary"
                  onClick={handleRefresh}
                >
                  <RefreshIcon />
                </LoadingIconButton>
                <Box
                  sx={{
                    flex: 1,
                  }}
                />
                <Chip
                  label="Mark all as Read"
                  icon={<RemoveRedEyeIcon />}
                  color="info"
                  onClick={() => markAllAsViewed()}
                  disabled={noNotifications || data?.unread.length === 0}
                />
                <Chip
                  label="Clear all"
                  icon={<DoNotDisturbIcon />}
                  color="error"
                  onClick={handleClearAllClick}
                  disabled={noNotifications}
                />
              </Stack>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  paddingLeft: 1,
                  paddingRight: 1,
                  minHeight: 50,
                  paddingBottom: 0.5,
                  alignItems: 'center',
                }}
              >
                <Grid
                  sx={{
                    width: 200,
                    marginBottom: 1,
                  }}
                >
                  <StateAutocomplete
                    selected={severity}
                    fieldLabel="Severity"
                    options={Object.values(NotificationSeverity)}
                    handleChange={handleSeverityChange}
                    variant="standard"
                  />
                </Grid>
                <Box
                  sx={{
                    flex: 1,
                  }}
                />
                <Grid>
                  <LoadingButton
                    loading={isLoading}
                    startIcon={<ChevronLeft />}
                    onClick={handleNewerClick}
                    disabled={page === 0}
                  >
                    Newer
                  </LoadingButton>
                </Grid>
                <Grid>
                  <Typography
                    sx={{
                      fontSize: 14,
                    }}
                  >
                    {page * PAGE_SIZE} - {min([(page + 1) * PAGE_SIZE, data?.count])} of{WHITESPACE}
                    {data?.count}
                  </Typography>
                </Grid>
                <Grid>
                  <LoadingButton
                    loading={isLoading}
                    startIcon={<ChevronRight />}
                    onClick={handleOlderClick}
                    disabled={(data?.count ?? 0) < (page + 1) * PAGE_SIZE}
                  >
                    Older
                  </LoadingButton>
                </Grid>
              </Stack>
              <Divider />
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {notificationEntries}
                {noNotifications && (
                  <Box
                    sx={{
                      padding: 1,
                      paddingBottom: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 20,
                      }}
                    >
                      All clear! <Emoji symbol="🚀" />
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </>
  );
}

export default Notifications;
